import React, { useEffect, CSSProperties } from 'react';
import { Layout, Spin, Flex, Drawer } from 'antd';
import { useAppSelector } from '../../shared/hooks/use-app-selector';
import { useLocalStorageState } from 'ahooks';
import { Header as NewAppHeader } from '../../app/shell/Header';
import { SideMenu } from '../../app/shell/side-menu/SideMenu';

import { useScreenName } from '../../shared/hooks/use-screen-name';
import { FlatSideMenu } from '../../app/shell/flat-side-menu/FlatSideMenu';
import { isWsbMerchant } from '../../selectors';
import { GRAVITY_ACTIVATION_CLASS } from '../../shared/constants/merchant';
import { AIAssistant } from '../../app/shell/AIAssistant';
import { useAIAssistant } from '../../shared/contexts/AIAssistantContext';
import { BETA_TOGGLES } from '../../features/beta/screens/Beta';
import { BookingsUpgradeModalContainer } from '../../features/bookings-upgrade/containers';
import { useMobileOverlay } from '../../hooks/use-mobile-overlay';

const { Content: AntdContent } = Layout;

// PageLayout Styles
const PAGE_LAYOUT_STYLES = {
  loadingContainer: {
    height: '100vh',
  } as CSSProperties,
  mainContent: {
    display: 'flex',
    margin: '0 auto',
    width: '100%',
    height: '100%',
  } as CSSProperties,
  pageContent: {
    height: '100%',
    flex: 1,
  } as CSSProperties,
  defaultLayout: {
    minHeight: '100vh',
  } as CSSProperties,
  mobileMenuLayout: {
    height: '100%',
    background: 'transparent',
  } as CSSProperties,
};

// The menu supplies its own logo-and-close row, so the drawer contributes no
// chrome of its own — just an unpadded body.
const MOBILE_MENU_DRAWER_STYLES = {
  body: { padding: 0 },
};

export function PageLayout({
  children,
  apiClient: _apiClient,
  trackMethod: _trackMethod,
  eventBus: _eventBus,
}: {
  children: JSX.Element;
  apiClient: unknown;
  trackMethod: (
    eventName: string,
    eventPayload?: string | number | object
  ) => void;
  eventBus: unknown;
}) {
  // Below `md` the side menu would eat 80px of an already narrow viewport while
  // being unusable (labels hidden, no way to expand it — the header toggle is
  // desktop-only). Move it into a full-width drawer instead so the page content
  // gets the whole screen.
  const {
    isMobile,
    isOpen: isMobileMenuOpen,
    open: openMobileMenu,
    close: closeMobileMenu,
  } = useMobileOverlay();
  const currentAccount = useAppSelector((state) => state.currentAccount);
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const isWsb = useAppSelector(isWsbMerchant);

  const screenName = useScreenName();

  const [sideMenuCollapsed, setSideMenuCollapsed] = useLocalStorageState(
    'sideMenuCollapsed',
    {
      defaultValue: isMobile || false,
      listenStorageChange: true,
    }
  );

  const [showOnboardingAIAssistant] = useLocalStorageState<boolean>(
    BETA_TOGGLES.smartOnboardingAIAssistant.key,
    {
      listenStorageChange: true,
    }
  );

  const { isOpen: isAIAssistantOpen } = useAIAssistant();

  const isUserEligibleForAIAssistant =
    (showOnboardingAIAssistant ||
      (currentAccount?.locale?.includes('en') &&
        currentMerchant?.address?.country === 'DE')) &&
    isAIAssistantOpen;

  // Collapse menu when screen is smaller than md
  useEffect(() => {
    if (sideMenuCollapsed === undefined) {
      setSideMenuCollapsed(isMobile);
    }
  }, [isMobile]);

  const merchantId = currentMerchant?.id || '';

  if (!merchantId) {
    return (
      <Layout>
        <Flex
          align="center"
          justify="center"
          style={PAGE_LAYOUT_STYLES.loadingContainer}
        >
          <Spin></Spin>
        </Flex>
      </Layout>
    );
  }

  return isWsb ? (
    <main id="main-content" style={PAGE_LAYOUT_STYLES.mainContent}>
      <div className={GRAVITY_ACTIVATION_CLASS}>
        <FlatSideMenu />
      </div>
      <div className="as-PageContent" style={PAGE_LAYOUT_STYLES.pageContent}>
        {children}
      </div>
      <BookingsUpgradeModalContainer />
    </main>
  ) : (
    <Layout className="as-PageLayout" style={PAGE_LAYOUT_STYLES.defaultLayout}>
      {!isMobile && (
        <SideMenu
          screenName={screenName}
          sideMenuCollapsed={sideMenuCollapsed}
        />
      )}
      <Layout>
        <NewAppHeader
          sideMenuCollapsed={sideMenuCollapsed}
          onSideMenuCollapseChange={setSideMenuCollapsed}
          onMobileMenuOpen={openMobileMenu}
        />
        <Layout>
          <AntdContent className="as-PageContent">{children}</AntdContent>
          {isUserEligibleForAIAssistant && <AIAssistant />}
        </Layout>
      </Layout>
      {isMobile && (
        <Drawer
          placement="left"
          width="100%"
          open={isMobileMenuOpen}
          onClose={closeMobileMenu}
          styles={MOBILE_MENU_DRAWER_STYLES}
          // No antd header: it would stack a close-only row on top of the
          // menu's own logo row, costing ~56px before the first menu item. The
          // menu renders the close button beside its logo instead.
          closable={false}
        >
          {/* The menu is an antd Sider, which registers itself with the
              nearest Layout through context — including across the drawer's
              portal. Give it a Layout of its own so it does not flip the page
              layout into its `has-sider` variant. */}
          <Layout style={PAGE_LAYOUT_STYLES.mobileMenuLayout}>
            <SideMenu
              screenName={screenName}
              sideMenuCollapsed={false}
              fullWidth
              onNavigate={closeMobileMenu}
              onClose={closeMobileMenu}
            />
          </Layout>
        </Drawer>
      )}
      {/* Mounted in both layouts so it appears over whatever screen the merchant
          is on, per the design. Self-gating — renders nothing unless eligible,
          which today means WSB only, so the non-WSB branch is future-proofing. */}
      <BookingsUpgradeModalContainer />
    </Layout>
  );
}
